import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations, { Lang, isRTL } from './translations';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
  rtl: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'fr',
  setLang: () => {},
  t: (key: string) => key,
  rtl: false,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem('lavallee_lang') as Lang;
      if (saved && translations[saved]) return saved;
    } catch {}
    return 'fr';
  });

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    try { localStorage.setItem('lavallee_lang', newLang); } catch {}
  }, []);

  const t = useCallback((key: string): string => {
    return translations[lang]?.[key] || translations['fr']?.[key] || key;
  }, [lang]);

  const rtl = isRTL(lang);

  useEffect(() => {
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang, rtl]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, rtl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
